import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Navigation from '../components/Navigation';
import { Users, Settings, UserPlus, Trash2, Loader2, RefreshCw, Shield, User, Mail, Key, Calendar, X, Database, FileText, CheckSquare, Clock, Briefcase, Bot } from 'lucide-react';
import { toast } from '../lib/toast';

interface UserAccount {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  role_name: string;
}

interface SuperAdminProps {
  navigate: (to: string) => void;
}

export default function SuperAdmin({ navigate }: SuperAdminProps) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'admin'
  });
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalProjects: 0,
    totalTasks: 0,
    completedTasks: 0,
    totalAgencies: 0,
    totalNotes: 0,
    totalAIAgents: 0,
    totalTeamMembers: 0
  });
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    checkSuperAdmin();
    fetchUsers();
    fetchStats();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }
      
      try {
        // Check if user is super admin
        const { data: profile } = await supabase
          .from('profiles')
          .select('role_id')
          .eq('user_id', user.id)
          .single();
  
        if (profile && profile.role_id) {
          const { data: role } = await supabase
            .from('roles')
            .select('name')
            .eq('id', profile.role_id)
            .single();
  
          if (role && role.name === 'super_admin') {
            setIsSuperAdmin(true);
          } else {
            setIsSuperAdmin(false);
            navigate('/overview');
          }
        }
      } catch (error) {
        console.error('Error checking role:', error);
        setIsSuperAdmin(false);
        navigate('/overview');
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
      navigate('/overview');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Use the RPC function to get admin accounts
      const { data, error } = await supabase.rpc('get_admin_accounts');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load user accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Get total users count
      const { count: totalUsers, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Get active users (logged in within last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: activeUsersData, error: activeUsersError } = await supabase
        .from('profiles') 
        .select('user_id')
        .gt('updated_at', thirtyDaysAgo.toISOString());

      // Get total projects
      const { count: totalProjects, error: projectsError } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true });

      // Get total tasks
      const { count: totalTasks, error: tasksError } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true });

      // Get completed tasks
      const { count: completedTasks, error: completedTasksError } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('completed', true);
        
      // Get total agencies
      const { count: totalAgencies, error: agenciesError } = await supabase
        .from('agencies')
        .select('*', { count: 'exact', head: true });
        
      // Get total notes
      const { count: totalNotes, error: notesError } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true });
        
      // Get total AI agents
      const { count: totalAIAgents, error: aiAgentsError } = await supabase
        .from('ai_agents')
        .select('*', { count: 'exact', head: true });
        
      // Get total team members
      const { count: totalTeamMembers, error: teamMembersError } = await supabase
        .from('team_members')
        .select('*', { count: 'exact', head: true });

      if (usersError || activeUsersError || projectsError || tasksError || completedTasksError || 
          agenciesError || notesError || aiAgentsError || teamMembersError) {
        throw new Error('Error fetching statistics');
      }

      setStats({
        totalUsers: totalUsers || 0,
        activeUsers: activeUsersData?.length || 0,
        totalProjects: totalProjects || 0,
        totalTasks: totalTasks || 0,
        completedTasks: completedTasks || 0,
        totalAgencies: totalAgencies || 0,
        totalNotes: totalNotes || 0,
        totalAIAgents: totalAIAgents || 0,
        totalTeamMembers: totalTeamMembers || 0
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Don't show error toast to avoid disrupting the UI
      // toast.error('Error fetching statistics');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newUser.email || !newUser.password || !newUser.first_name || !newUser.last_name) {
      setError('All fields are required');
      return;
    }

    setCreating(true);
    try {
      // Create user with auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            first_name: newUser.first_name,
            last_name: newUser.last_name,
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Set role for the new user
        const { data: roles } = await supabase
          .from('roles')
          .select('id')
          .eq('name', newUser.role)
          .single();

        if (roles) {
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ 
              role_id: roles.id,
              first_name: newUser.first_name,
              last_name: newUser.last_name
            }, { returning: 'minimal' })
            .eq('user_id', authData.user.id); 

          if (updateError) throw updateError;
          
          // Fetch users again to update the list
          await fetchUsers();
        }
        setShowCreateModal(false);
        setNewUser({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          role: 'admin'
        });
        fetchUsers();
        
        toast.success('User account created successfully');
        fetchStats();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to create user');
      toast.error('Failed to create user account');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user account? This action cannot be undone.')) {
      return;
    }

    setDeleting(userId);
    try {
      const { error } = await supabase.rpc('delete_auth_user', {
        user_id_to_delete: userId
      });

      if (error) throw error;
      
      toast.success('User account deleted successfully');
      setUsers(users.filter(user => user.id !== userId));
      fetchStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user account');
    } finally {
      setDeleting(null);
    }
  };

  if (!isSuperAdmin) {
    // Return null while checking permissions to avoid flash of "access denied"
    return null;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Super Admin Dashboard</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <UserPlus className="h-5 w-5 mr-2" />
              Create Admin Account
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`${
                  activeTab === 'dashboard'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                User Management
              </button>
              <button
                onClick={() => setActiveTab('database')}
                className={`${
                  activeTab === 'database'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Database Stats
              </button>
            </nav>
          </div>

          {activeTab === 'dashboard' && (
            <>
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30 rounded-md p-3">
                      <Users className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Users</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalUsers}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                      <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Active Users</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.activeUsers}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                      <Briefcase className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Projects</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalProjects}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                      <CheckSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Tasks</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalTasks}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                      <CheckSquare className="h-6 w-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Completed Tasks</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.completedTasks}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/30 rounded-md p-3">
                      <Users className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Agencies</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalAgencies}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-pink-100 dark:bg-pink-900/30 rounded-md p-3">
                      <FileText className="h-6 w-6 text-pink-600 dark:text-pink-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Notes</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalNotes}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-indigo-100 dark:bg-indigo-900/30 rounded-md p-3">
                      <Bot className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">AI Agents</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalAIAgents}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-cyan-100 dark:bg-cyan-900/30 rounded-md p-3">
                      <Users className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Team Members</dt>
                        <dd>
                          <div className="text-lg font-medium text-gray-900 dark:text-white">{stats.totalTeamMembers}</div>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Task Completion Rate */}
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Task Completion Rate</h3>
                <div className="relative pt-1">
                  <div className="flex mb-2 items-center justify-between">
                    <div>
                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900 dark:text-indigo-300">
                        Progress
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-300">
                        {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/30">
                    <div 
                      style={{ width: `${stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) * 100 : 0}%` }} 
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500"
                    ></div>
                  </div>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <div>Completed: {stats.completedTasks}</div>
                  <div>Total: {stats.totalTasks}</div>
                </div>
              </div>
              
              {/* Recent Activity */}
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">System Status</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">Database</span>
                    </div>
                    <span className="text-sm text-green-600 dark:text-green-400">Operational</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">Authentication</span>
                    </div>
                    <span className="text-sm text-green-600 dark:text-green-400">Operational</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">Storage</span>
                    </div>
                    <span className="text-sm text-green-600 dark:text-green-400">Operational</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">API</span>
                    </div>
                    <span className="text-sm text-green-600 dark:text-green-400">Operational</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'users' && (
            <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Admin Accounts</h2>
                <button
                  onClick={fetchUsers}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
                >
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  Refresh
                </button>
              </div>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              ) : users.length === 0 ? (
                <div className="p-6 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No user accounts found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Role
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Created
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Last Sign In
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 dark:bg-indigo-900/50 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {user.first_name} {user.last_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role_name === 'super_admin' 
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200'
                            }`}>
                              {user.role_name === 'super_admin' ? (
                                <Shield className="h-3 w-3 mr-1" />
                              ) : (
                                <User className="h-3 w-3 mr-1" />
                              )}
                              {user.role_name === 'super_admin' ? 'Super Admin' : 'Admin'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {user.role_name !== 'super_admin' && (
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={deleting === user.id}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                {deleting === user.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'database' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Database Statistics</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Overview of database tables and record counts.</p>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Database className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Users</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalUsers}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total registered users</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Briefcase className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Projects</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalProjects}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total projects created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <CheckSquare className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Tasks</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalTasks}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total tasks created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Agencies</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalAgencies}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total agencies created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-pink-600 dark:text-pink-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Notes</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalNotes}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total notes created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Bot className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">AI Agents</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalAIAgents}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total AI agents created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Users className="h-5 w-5 text-cyan-600 dark:text-cyan-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Team Members</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.totalTeamMembers}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total team members created</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Completed Tasks</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.completedTasks}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Total completed tasks</p>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-2" />
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">Active Users</h4>
                      </div>
                      <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.activeUsers}</p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Users active in last 30 days</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Database Health</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Status of database connections and performance.</p>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Database Connection</span>
                      </div>
                      <span className="text-sm text-green-600 dark:text-green-400">Healthy</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Query Performance</span>
                      </div>
                      <span className="text-sm text-green-600 dark:text-green-400">Optimal</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Storage Usage</span>
                      </div>
                      <span className="text-sm text-green-600 dark:text-green-400">Normal</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Backup Status</span>
                      </div>
                      <span className="text-sm text-green-600 dark:text-green-400">Up to date</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Create Admin Account
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Email
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Password
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Key className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      id="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Role
                  </label>
                  <select
                    id="role"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400 dark:text-red-300" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                          {error}
                        </h3>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}